import { Category, ItemConditionLike } from "../types";
import { Chat } from "../types/chat";
import { ENVIRONMENT } from "./constants";
import { format, isSameYear, isToday, isYesterday } from "date-fns";

export const formatCurrency = (amount: number, currency = "USD"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(amount);
};

export const formatDate = (date: Date | string, format = "short"): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: format as "short" | "medium" | "long" | "full",
  }).format(dateObj);
};

export const formatDateTime = (
  dateString?: string,
  monthFormat?: "short" | "long",
  dayFormat?: "numeric",
  yearFormat?: "numeric",
  hourFormat?: "2-digit",
  minuteFormat?: "2-digit"
): string => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {};

  if (monthFormat) options.month = monthFormat;
  if (dayFormat) options.day = dayFormat;
  if (yearFormat) options.year = yearFormat;
  if (hourFormat) options.hour = hourFormat;
  if (minuteFormat) options.minute = minuteFormat;

  return date.toLocaleDateString("en-US", options);
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
};

export const getConditionDisplayName = (
  conditionId: string,
  conditions: ItemConditionLike[]
): string => {
  const condition = conditions.find((itemCondition) => itemCondition.id === conditionId);
  return condition ? condition.name : conditionId;
};

export const sortChatsByLastActivity = (chats: Chat[]): Chat[] => {
  return [...chats].sort((a, b) => {
    const ta = a.lastMessageTime ?? a.lastActivity ?? 0;
    const tb = b.lastMessageTime ?? b.lastActivity ?? 0;
    return tb - ta;
  });
}

export const parseChannelData = (raw: unknown): unknown => {
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw;
}

export const isDevelopmentEnv = (): boolean =>
  process.env.NODE_ENV === ENVIRONMENT.DEVELOPMENT;

export const logErrorDev = (message: string, error: unknown) => {
  if (isDevelopmentEnv()) {
    // eslint-disable-next-line
    console.log(message, error);
  }
}

export const resolveCategoryName = (categoryId: string, categoryName: string, categories: Category[]) => {
  if (categoryName) {
    return categoryName;
  }
  if (!categoryId) {
    return "";
  }
  const matched = categories.find((category) => category.categoryId === categoryId);
  return matched?.categoryName || "";
}

export const normalizeSelectedIds = (values?: string[]) =>
  Array.isArray(values)
    ? values.filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    : [];

export const formatMessageTimestamp = (timestamp: number | string | Date): string => {
  const date = new Date(timestamp);
  if (isToday(date)) {
    return format(date, "HH:mm");
  }
  if (isYesterday(date)) {
    return `Yesterday ${format(date, "HH:mm")}`;
  }
  if (isSameYear(date, new Date())) {
    return format(date, "MMM d, HH:mm");
  }
  return format(date, "MMM d, yyyy HH:mm");
};

export const formatChatConversationTime = (lastTime: number | undefined): string => {
  if (lastTime == null || lastTime === 0) {
    return "";
  }
  const date = new Date(lastTime);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  if (isToday(date)) {
    return format(date, "HH:mm");
  }
  return formatDateTime(
    date.toISOString(),
    "short",
    "numeric",
    "numeric"
  );
};
